import { useMemo, useState } from 'react';
import {
  Box,
  Center,
  Group,
  Loader,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconArrowsSort, IconSortAscending, IconSortDescending } from '@tabler/icons-react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { EmptyState } from './EmptyState';

export type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  /** Row striping and colouring, for example a red row for a grounded aircraft. */
  rowProps?: (row: T) => { bg?: string; opacity?: number };
  emptyTitle?: string;
  emptyHint?: string;
  /** Server-side paging. Omit for a table that is short enough to render whole. */
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
  };
  /** Sorting handled by the caller, usually as a DRF `ordering` parameter. */
  sorting?: { value: SortingState; onChange: (state: SortingState) => void };
  /** A totals row, which a logbook page always carries. */
  footer?: React.ReactNode;
  stickyHeader?: boolean;
  minWidth?: number;
};

const PAGE_SIZES = ['25', '50', '100', '200'];

/**
 * The grid every list screen uses.
 *
 * TanStack Table drives the column model and Mantine's `Table` does the rendering, which
 * keeps the dense logbook and maintenance views on the same primitives as the rest of the UI.
 */
export function DataTable<T>({
  data,
  columns,
  isLoading,
  onRowClick,
  rowProps,
  emptyTitle,
  emptyHint,
  pagination,
  sorting,
  footer,
  stickyHeader = true,
  minWidth,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const [localSorting, setLocalSorting] = useState<SortingState>([]);
  const isServerSorted = Boolean(sorting);

  const table = useReactTable({
    data,
    columns,
    state: { sorting: sorting?.value ?? localSorting },
    onSortingChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(sorting?.value ?? localSorting) : updater;
      if (sorting) sorting.onChange(next);
      else setLocalSorting(next);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isServerSorted ? undefined : getSortedRowModel(),
    manualSorting: isServerSorted,
  });

  const pageCount = useMemo(
    () => (pagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 1),
    [pagination],
  );

  const rows = table.getRowModel().rows;

  return (
    <Paper radius="md" style={{ overflow: 'hidden' }}>
      <ScrollArea.Autosize mah="calc(100vh - 320px)" type="auto">
        <Table
          striped
          highlightOnHover={Boolean(onRowClick)}
          stickyHeader={stickyHeader}
          horizontalSpacing="sm"
          verticalSpacing={6}
          fz="sm"
          miw={minWidth}
        >
          <Table.Thead>
            {table.getHeaderGroups().map((group) => (
              <Table.Tr key={group.id}>
                {group.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const direction = header.column.getIsSorted();
                  const meta = header.column.columnDef.meta as
                    | { align?: 'left' | 'right' | 'center'; width?: number | string }
                    | undefined;
                  return (
                    <Table.Th
                      key={header.id}
                      w={meta?.width}
                      ta={meta?.align}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <UnstyledButton
                          onClick={header.column.getToggleSortingHandler()}
                          fz="xs"
                          fw={600}
                          tt="uppercase"
                          c="dimmed"
                        >
                          <Group gap={4} wrap="nowrap" justify={meta?.align}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {direction === 'asc' ? (
                              <IconSortAscending size={13} />
                            ) : direction === 'desc' ? (
                              <IconSortDescending size={13} />
                            ) : (
                              <IconArrowsSort size={13} opacity={0.4} />
                            )}
                          </Group>
                        </UnstyledButton>
                      ) : (
                        <Text fz="xs" fw={600} tt="uppercase" c="dimmed">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Text>
                      )}
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Center py="xl">
                    <Loader size="sm" />
                  </Center>
                </Table.Td>
              </Table.Tr>
            ) : rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <EmptyState
                    title={emptyTitle ?? t('common.noResults')}
                    hint={emptyHint ?? t('common.noResultsHint')}
                  />
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row) => {
                const extra = rowProps?.(row.original) ?? {};
                return (
                  <Table.Tr
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    style={{ cursor: onRowClick ? 'pointer' : undefined, ...extra }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta as
                        | { align?: 'left' | 'right' | 'center'; mono?: boolean }
                        | undefined;
                      return (
                        <Table.Td
                          key={cell.id}
                          ta={meta?.align}
                          ff={meta?.mono ? 'monospace' : undefined}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
          {footer ? <Table.Tfoot>{footer}</Table.Tfoot> : null}
        </Table>
      </ScrollArea.Autosize>

      {pagination && pagination.total > 0 ? (
        <Group justify="space-between" px="md" py="xs" wrap="wrap" gap="sm">
          <Text fz="xs" c="dimmed">
            {t('common.showingRange', {
              from: (pagination.page - 1) * pagination.pageSize + 1,
              to: Math.min(pagination.page * pagination.pageSize, pagination.total),
              count: pagination.total,
            })}
          </Text>
          <Group gap="sm">
            {pagination.onPageSizeChange ? (
              <Select
                size="xs"
                w={92}
                data={PAGE_SIZES}
                value={String(pagination.pageSize)}
                onChange={(value) => value && pagination.onPageSizeChange?.(Number(value))}
                aria-label={t('common.rowsPerPage')}
                allowDeselect={false}
              />
            ) : null}
            <Pagination
              size="sm"
              total={pageCount}
              value={pagination.page}
              onChange={pagination.onPageChange}
              withEdges={pageCount > 5}
            />
          </Group>
        </Group>
      ) : (
        <Box h={4} />
      )}
    </Paper>
  );
}
